import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck, Phone, Star } from "lucide-react";
import { BOOKSY_URL, PHONE_TEL, HERO_IMAGE } from "@/data/content";
import { Logo } from "@/components/Logo";

const EASE = [0.22, 1, 0.36, 1];

const lineReveal = {
    hidden: { y: "115%" },
    visible: (i) => ({
        y: "0%",
        transition: { duration: 0.9, delay: 0.4 + i * 0.15, ease: EASE },
    }),
};

const fadeUp = {
    hidden: { opacity: 0, y: 26 },
    visible: (delay) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay, ease: EASE },
    }),
};

export const Hero = () => {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 180]);

    return (
        <section
            id="inicio"
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden"
        >
            <motion.img
                src={HERO_IMAGE}
                alt="Coche negro brillante recién detallado"
                initial={{ scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.8, ease: EASE }}
                style={{ y: bgY }}
                className="absolute inset-0 h-[115%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />

            <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8">
                <div className="max-w-3xl">
                    <motion.div variants={fadeUp} custom={0} initial="hidden" animate="visible">
                        <Logo testId="hero-logo" className="mb-10 h-24 w-auto sm:h-32" />
                    </motion.div>

                    <motion.p
                        variants={fadeUp}
                        custom={0.18}
                        initial="hidden"
                        animate="visible"
                        className="gold-text mb-4 text-xs font-bold uppercase tracking-[0.3em] sm:text-sm"
                    >
                        Autolavado · Detailing · León
                    </motion.p>

                    <motion.div
                        data-testid="hero-gold-line"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1, delay: 0.28, ease: EASE }}
                        className="mb-7 h-px w-24 origin-left bg-gold"
                    />

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#F3F4F6] sm:text-5xl lg:text-6xl"
                    >
                        <span className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                variants={lineReveal}
                                custom={0}
                                initial="hidden"
                                animate="visible"
                            >
                                Tu coche merece
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span
                                className="gold-text block"
                                variants={lineReveal}
                                custom={1}
                                initial="hidden"
                                animate="visible"
                            >
                                el mejor cuidado
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        variants={fadeUp}
                        custom={0.85}
                        initial="hidden"
                        animate="visible"
                        data-testid="hero-subheadline"
                        className="mt-6 max-w-xl text-base leading-relaxed text-gray-300 md:text-lg"
                    >
                        Lavado, detailing y tratamientos premium en León — trato personalizado,
                        resultados profesionales
                    </motion.p>

                    <motion.div
                        variants={fadeUp}
                        custom={1}
                        initial="hidden"
                        animate="visible"
                        className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <a
                            href={BOOKSY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-booking-btn"
                            className="inline-flex items-center justify-center gap-2 rounded-sm bg-gold px-8 py-4 text-base font-bold text-ink shadow-xl shadow-black/40 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#a88a1e]"
                        >
                            <CalendarCheck className="h-5 w-5" />
                            Reservar Cita
                        </a>
                        <a
                            href={PHONE_TEL}
                            data-testid="hero-call-btn"
                            className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/30 bg-white/5 px-8 py-4 text-base font-bold text-white backdrop-blur-sm transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white/15"
                        >
                            <Phone className="h-5 w-5" />
                            Llamar Ahora
                        </a>
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        custom={1.15}
                        initial="hidden"
                        animate="visible"
                        data-testid="hero-trust-badge"
                        className="mt-8 inline-flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-sm"
                    >
                        <span className="flex items-center gap-0.5" aria-hidden="true">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    className={`h-4 w-4 ${i < 4 ? "fill-gold text-gold" : "fill-gold/40 text-gold/40"}`}
                                />
                            ))}
                        </span>
                        <span className="text-sm font-semibold text-gray-200">
                            4,7/5 en Google (13 reseñas)
                        </span>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 0.8 }}
                className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
                data-testid="hero-scroll-cue"
            >
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
                    Desliza
                </span>
                <motion.span
                    animate={{ scaleY: [0.3, 1, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="h-8 w-px origin-top bg-gold/70"
                />
            </motion.div>
        </section>
    );
};
